import { UserListUser } from "@/types/user";
import { faCaretDown } from "@fortawesome/pro-solid-svg-icons/faCaretDown";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Combobox, Loader, Pill, PillsInput, useCombobox } from "@mantine/core";
import escapeRegExp from "lodash/escapeRegExp";
import orderBy from "lodash/orderBy";
import { useCallback, useMemo, useState } from "react";

import { useUsersQuery } from "@/hooks/queries/user.queries";
import { TestID } from "@/utils/helpers";
import { AssignUserOption } from "./assign-user-option";
import { AssignUserSingleSelectInput } from "./single-select-input";

interface AssignUserSelectorProps {
  baseTestId: TestID;
  fullWidth?: boolean;
  // Because this selector may be used multiple times within a single view (for
  // example, in a filter and in the Assigned To cell), we take a keyPrefix prop
  // to help ensure unique keys across usages
  keyPrefix: string;
  loading?: boolean;
  multi?: boolean;
  onChange: (id: string) => void;
  // If provided, preempts this component querying for the full list of users
  // or built in "Not assigned" in favor of using the override
  usersOverride?: UserListUser[];
  value: string[];
}

export const NOT_ASSIGNED = "Not assigned";

export const AssignUserSelector = ({
  baseTestId,
  fullWidth = false,
  keyPrefix,
  loading = false,
  multi = false,
  onChange,
  usersOverride,
  value,
}: AssignUserSelectorProps) => {
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
    },
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });
  const { data: userResponse = [], isPending } = useUsersQuery({
    enabled: !usersOverride,
  });
  const users = useMemo(
    () => usersOverride ?? userResponse ?? [],
    [userResponse, usersOverride],
  );
  const isLoading = isPending || loading;
  const inputTestId = useMemo(
    () => baseTestId.addComponent({ element: "input" }).id,
    [baseTestId],
  );
  const orderedUsers = useMemo(() => orderBy(users, "email"), [users]);
  const userMap = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users],
  );
  const selectedUserDisplayValue = useMemo(() => {
    const user = userMap.get(value[0]);

    return user?.fullName ?? user?.email ?? "";
  }, [userMap, value]);
  const [search, setSearch] = useState("");
  const [singleSelectSearchActive, setSingleSelectSearchActive] =
    useState(false);
  const optionSearch = multi || singleSelectSearchActive ? search : "";
  const handleSearchChange = useCallback((search: string) => {
    setSingleSelectSearchActive(true);
    setSearch(search);
  }, []);

  const getKey = useCallback(
    (key: string) => `${keyPrefix}_${key}`,
    [keyPrefix],
  );

  const options = useMemo(() => {
    if (!combobox.dropdownOpened) {
      return [];
    }

    const filteredUsers = orderedUsers.filter(({ email, fullName }) => {
      const pattern = escapeRegExp(optionSearch);
      const searchRegex = new RegExp(pattern, "i");

      return (
        !optionSearch ||
        searchRegex.test(email) ||
        searchRegex.test(fullName ?? "")
      );
    });
    // When the BE provides a list of available filters, they include an option
    // for "Not assigned"
    const base = Array.isArray(usersOverride)
      ? []
      : [
          <AssignUserOption
            className={filteredUsers.length === 0 && "border-none"}
            key={getKey(NOT_ASSIGNED)}
            multi={multi}
            primaryText={multi ? NOT_ASSIGNED : "None"}
            selected={value.includes(NOT_ASSIGNED)}
            testId={
              baseTestId
                .addComponent({ element: "option" })
                .addDiscriminator("not-assigned").id
            }
            value={NOT_ASSIGNED}
          />,
        ];

    return base.concat(
      filteredUsers.map(({ email, fullName, id }, index) => (
        <AssignUserOption
          className={index === filteredUsers.length - 1 && "border-none"}
          key={getKey(id)}
          multi={multi}
          primaryText={email}
          secondaryText={fullName ?? "No name provided"}
          selected={value.includes(id)}
          testId={
            baseTestId
              .addComponent({ element: "option" })
              .addDiscriminator(index).id
          }
          value={id}
        />
      )),
    );
  }, [
    baseTestId,
    combobox.dropdownOpened,
    getKey,
    multi,
    optionSearch,
    orderedUsers,
    usersOverride,
    value,
  ]);

  ////////////////////
  //// Multi Only ////
  ////////////////////
  const handleRemove = useCallback(
    (id?: string) => {
      const idToRemove = id ?? value[value.length - 1];

      if (idToRemove) {
        onChange(idToRemove);
      }
    },
    [onChange, value],
  );

  const selectedUserPills = multi
    ? value.map((userId, index) => {
        const user = userMap.get(userId);

        return user ? (
          <Pill
            data-testid={
              baseTestId
                .addComponent({ element: "selected-option" })
                .addDiscriminator(index).id
            }
            key={user.id}
            withRemoveButton
            onRemove={() => handleRemove(userId)}
          >
            {user.fullName || user.email}
          </Pill>
        ) : null;
      })
    : null;

  /////////////////////
  //// Single Only ////
  /////////////////////
  const [optimisticSearch, setOptimisticSearch] = useState("");

  return (
    <Combobox
      dropdownPadding={0}
      onOptionSubmit={(selectedUserId) => {
        if (!multi) {
          const user = userMap.get(selectedUserId);
          setOptimisticSearch(user?.fullName ?? user?.email ?? "");
          setSearch("");
          setSingleSelectSearchActive(false);

          // Automatically close dropdown on selection when in single select
          combobox.closeDropdown();
        }

        onChange(selectedUserId);
      }}
      position="bottom-start"
      store={combobox}
      width={300}
      withinPortal
    >
      {multi ? (
        <Combobox.DropdownTarget>
          <PillsInput
            data-testid={inputTestId}
            leftSection={
              isLoading ? (
                <Loader size="xs" />
              ) : (
                <FontAwesomeIcon icon={faUser} />
              )
            }
            onClick={() => combobox.openDropdown()}
            rightSection={<FontAwesomeIcon icon={faCaretDown} />}
          >
            <Pill.Group>
              {value.includes(NOT_ASSIGNED) && (
                <Pill
                  data-testid={
                    baseTestId
                      .addComponent({ element: "selected-option" })
                      .addDiscriminator("not-assigned").id
                  }
                  withRemoveButton
                  onRemove={() => handleRemove(NOT_ASSIGNED)}
                >
                  {NOT_ASSIGNED}
                </Pill>
              )}
              {selectedUserPills}

              <Combobox.EventsTarget>
                <PillsInput.Field
                  disabled={isLoading}
                  onBlur={() => {
                    setSearch("");
                    combobox.closeDropdown();
                  }}
                  onChange={(event) => {
                    combobox.updateSelectedOptionIndex();
                    handleSearchChange(event.currentTarget.value);
                  }}
                  onFocus={() => combobox.openDropdown()}
                  onKeyDown={(event) => {
                    if (event.key === "Backspace" && search.length === 0) {
                      event.preventDefault();
                      handleRemove();
                    }
                  }}
                  placeholder="Search values"
                  value={search}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
        </Combobox.DropdownTarget>
      ) : (
        <AssignUserSingleSelectInput
          combobox={combobox}
          fullWidth={fullWidth}
          loading={isLoading}
          onBlur={() => {
            setSearch("");
            setSingleSelectSearchActive(false);
          }}
          search={
            isLoading
              ? optimisticSearch
              : singleSelectSearchActive
                ? search
                : selectedUserDisplayValue
          }
          setSearch={handleSearchChange}
          testId={inputTestId}
        />
      )}

      <Combobox.Dropdown>
        <Combobox.Options>
          {options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>Nothing found...</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
